// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp, resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductDescriptionStyles } from "@mcmc/Overrides/Components/ProductDescription";
import { productPriceStyles } from "@mcmc/Overrides/Components/ProductPrice";
import { mergeWithCss, RobotoCondensed } from "@mcmc/XDL/Utilities";
import { css } from "styled-components";

export const cardContainer: InjectableCss = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        flex-wrap: wrap;
                    `,
                ],
                "max",
            )}
    `,
};

export const numbersContainer: InjectableCss = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        margin: 0 -5px;
        & > div {
            flex: 1 1 50%;
            padding: 0 5px;
            min-width: 180px;
            &.flex-end {
                align-self: flex-end;
            }
        }
    `,
};

export const priceCheckboxContainer: InjectableCss = {
    css: css`
        ${numbersContainer.css}
        margin: -5px;
        padding-top: 5px;
        & > div {
            padding: 5px;
        }
    `,
};

export const imageWrapper: InjectableCss = {
    css: css`
        flex: 0 1 187px;
        border-right: 1px solid ${getColor("common.ui3")};

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        margin-right: 0px;
                        margin-bottom: 15px;
                        border-right: none;
                        flex-basis: 100%;
                    `,
                    null,
                    css`
                        flex: 1 0 120px;
                    `,
                ],
                "max",
            )}
    `,
};

export const cardInfoWrapper: InjectableCss = {
    css: css`
        flex: 5 0 300px;
        padding: 20px 20px 15px 20px;
        max-width: 100%;
        display: flex;
        flex-direction: column;
    `,
};

const commonDescriptionStyles = {
    css: css`
        width: 100%;
        border-bottom: 1px solid ${getColor("common.border")};
        margin-bottom: 15px;
        padding-bottom: 10px;
    `,
    typographyProps: {
        size: 18,
        lineHeight: "21px",
    },
};

export const descriptionStyles: ProductDescriptionStyles = {
    productDetailLink: commonDescriptionStyles,
    productDetailDescription: {
        ...commonDescriptionStyles.typographyProps,
        css: commonDescriptionStyles.css,
    },
};

export const cardActionsWrapper: InjectableCss = {
    css: css`
        display: flex;
        flex-direction: column;
        border-left: 1px solid ${getColor("common.ui3")};
        flex: 0 0 180px;
        align-items: flex-end;
        justify-content: flex-end;
        padding: 15px;

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        flex-direction: row;
                        flex-wrap: wrap;
                        border-top: 1px solid ${resolveColor("common.ui3", theme)};
                        flex-grow: 1;
                    `,
                ],
                "max",
            )}
    `,
};

export const unitOfMeasureSelect: SelectPresentationProps = {
    cssOverrides: {
        formField: css`
            ${getProp("theme.formField.defaultProps.cssOverrides.formField")}
            width: 70px;
            flex: 1;
            margin-right: 10px;
        `,
        inputSelect: css`
            ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
            padding-right: 15px !important;
        `,
        formInputWrapper: css`
            margin: 0 !important;
        `,
    },
    labelProps: {
        css: css`
            display: none;
        `,
    },
};

export const addToCartWrapper: InjectableCss = {
    css: css`
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding: 5px;
        align-self: stretch;

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        flex-grow: 1;
                    `,
                ],
                "max",
            )}
    `,
};

export const addToCartButton: ButtonPresentationProps = {
    sizeVariant: "medium",
    css: css`
        width: 100%;
        padding: 0 10px;
    `,
};

export const quantityOrdered: TextFieldProps = {
    labelPosition: "left",
    cssOverrides: {
        formField: css`
            width: 95px;
            padding: 5px;
        `,
        formInputWrapper: css`
            width: 55px;
        `,
    },
    labelProps: {
        size: 13,
        weight: 400,
        lineHeight: "16px",
        css: css`
            padding-right: 5px;
            width: auto;
        `,
    },
};

export const addToListWrapper: InjectableCss = {
    css: css`
        padding: 5px;
    `,
};

export const addToListLink: ProductAddToListLinkStyles = {
    link: {
        typographyProps: {
            size: 16,
            lineHeight: "28px",
        },
    },
};

const priceTextStyle: TypographyPresentationProps = {
    ...RobotoCondensed,
    size: 24,
    lineHeight: "28px",
    weight: "bold",
    css: css`
        white-space: nowrap;
    `,
};

export const price: ProductPriceStyles = {
    wrapper: {
        css: css``,
    },
    price: {
        priceText: priceTextStyle,
        realTimeText: mergeWithCss(priceTextStyle, {
            css: css`
                ${productPriceStyles.price?.realTimeText?.css}
            `,
        }),
        signInText: { weight: "bold" },
        errorText: { weight: "bold" },
        unitOfMeasureText: {
            size: 14,
            lineHeight: "20px",
            css: css`
                text-transform: capitalize;
            `,
        },
    },
    quoteMessage: {
        text: mergeWithCss(priceTextStyle, {
            size: 20,
            lineHeight: "24px",
            css: css`
                padding-right: 5px;
            `,
        }),
    },
};

export const sponsoredText: TypographyPresentationProps = {
    css: css`
        top: 0;
        left: 0;
        font-style: italic;
        position: absolute;
        z-index: 1;
        padding: 5px 10px;
        background: ${getColor("primary")};
        color: ${getColor("primary.contrast")};
        font-weight: bold;
    `,
};

export const productCardListStyles = {
    cardContainer,
    imageWrapper,
    cardInfoWrapper,
    descriptionStyles,
    cardActionsWrapper,
    unitOfMeasureSelect,
    addToCartWrapper,
    addToCartButton,
    quantityOrdered,
    numbersContainer,
    priceCheckboxContainer,
    addToListLink,
    addToListWrapper,
    price,
    sponsoredText,
};
